import { useState, useEffect, useCallback } from 'react'
import { toast } from 'react-toastify'
import {
  getMerchantsByNameLookup,
  getMerchantsByNameLookupOptions,
} from 'api/merchant'
import {
  MerchantLookupTypes,
  MerchantLookupTxSetFieldTypes,
  MerchantLookupTxFormTypes,
  MerchantLookupTxFormSubmitValuesTypes,
} from 'types/merchanTxTypes'

import { DropDownItem } from 'types/components'

const MerchantLookupTxINITVAL: MerchantLookupTxFormTypes = {
  value: '',
  field: '',
}

export const useMerchantLookupTx = (
  setFieldValue: MerchantLookupTxSetFieldTypes
): {
  merchants: MerchantLookupTypes[]
  isLoading: boolean
  initialValues: MerchantLookupTxFormTypes
  handleSubmitMerchantLookupTx: (
    values: MerchantLookupTxFormSubmitValuesTypes
  ) => void
  handleSelectMerchantResultTx: (
    merchantCode: number,
    merchantDeviceCode: string | number | null
  ) => void
  merchantLookupDropdownOptions: (inputParam: string) => Promise<DropDownItem[]>
  searchMerchants: (params: Record<string, string | null>) => Promise<MerchantLookupTypes[]>
} => {
  // STATES
  const [merchants, merchantsSet] = useState<MerchantLookupTypes[]>([])
  const [isLoading, isLoadingSet] = useState<boolean>(false)
  const [initialValues] = useState<MerchantLookupTxFormTypes>(
    {
      ...MerchantLookupTxINITVAL,
    }
  )

  const handleSelectMerchantResultTx = useCallback(
    (merchantCode: number, merchantDeviceCode: string | number | null) => {
      setFieldValue('storeId', merchantCode)
      setFieldValue('deviceCode', String(merchantDeviceCode))
    },
    [setFieldValue]
  )

  const handleSubmitMerchantLookupTx = useCallback(
    async (values: MerchantLookupTxFormSubmitValuesTypes) => {
      isLoadingSet(true)
      try {
        const merchants = await getMerchantsByNameLookup(
          `?${values.field}=${values.value}`
        )
        if (!merchants.length) throw 'No merchants found.'
        merchantsSet(merchants)
        toast.success('Merchant lookup result available.')
      } catch (error) {
        const message = JSON.parse(JSON.stringify(error))
        toast.error(`ERR: ${message.outcomeUserMessage}`)
      }
      isLoadingSet(false)
    },
    [isLoadingSet, merchantsSet]
  )

  const merchantLookupDropdownOptions = async (inputParam: string) => {
    if (inputParam.length < 3) return []
    const merchantQuery = `?merchantName=${inputParam}`
    const merchantLookup = await getMerchantsByNameLookupOptions(merchantQuery)
    return merchantLookup.map((item) => ({
      label: `${item.merchantName}  - ${item.deviceCode}`,
      value: `${String(item.traderId)}/${String(item.deviceCode)}`,
    }))
  }

  const searchMerchants = async (params: Record<string, string | null>) => {
    const merchantName = params.merchantName || params.name
    const traderId = params.traderId || params.id
    const merchantCode = params.merchantCode || params.mcode
    const query = new URLSearchParams()

    if (traderId) query.set('traderId', traderId)
    if (merchantName) query.set('merchantName', merchantName)
    if (merchantCode) query.set('merchantCode', merchantCode)

    return getMerchantsByNameLookupOptions(`?${query.toString()}`)
  }

  return {
    merchants,
    isLoading,
    initialValues,
    handleSelectMerchantResultTx,
    handleSubmitMerchantLookupTx,
    merchantLookupDropdownOptions,
    searchMerchants,
  }
}
